// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { portfolioApi } from '@shared/data-access/api/ai/portfolio-api';
import {
  type StockAnalysis,
  type EtfAnalysis,
  type MutualFundAnalysis,
} from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import { type Holding } from '@shared/data-access/interfaces/redux/portfolio-rebalance-state';
import { type TargetPortfolioPositionWithAssetInfo } from '@shared/data-access/transformers/proposals/build-trader-target-portfolio';
import { useSafeUpdate, groupBy } from '@shared/ui-kit';
import { useState, useMemo, useCallback, useEffect } from 'react';

interface AssetAnalysisResp {
  stock: StockAnalysis | null | any;
  etf: EtfAnalysis | null;
  fund: MutualFundAnalysis | null;
}

export interface UseAggregatorAllocationAssetAnalysisProps {
  holdings: TargetPortfolioPositionWithAssetInfo[];
  hist: number;
  benchmark: Array<{ ticker: string; weight: number }>;
}

export const useTraderAllocationAnalysis = ({
  holdings,
  hist,
  benchmark,
}: UseAggregatorAllocationAssetAnalysisProps) => {
  const runSafeUpdate = useSafeUpdate();

  const [getAnalysis, analysisQuery] = portfolioApi.useLazyPortfolioAnalysisQuery();

  const [assetAnalysis, setAssetAnalysis] = useState<AssetAnalysisResp>({
    stock: null,
    etf: null,
    fund: null,
  });

  const groupedPositions = useMemo(
    () => groupBy(holdings, 'asset_type'),
    [holdings],
  );

  const getAnalysisData = useCallback(async () => {
    try {
      for (const [assetType, positions] of groupedPositions.entries()) {
        /* istanbul ignore next */
        const filteredPositions = positions.map(
          (position): Holding => ({
            ticker: position.ticker ?? position.isin!,
            price: position.amount / position.quantity,
            qty: position.quantity,
            unrealized_capital_gains: 0,
          }),
        );

        /* istanbul ignore if */
        if (filteredPositions.length === 0) continue;

        const mapAssetType: Record<string, string> = {
          stock: 'stock',
          etf: 'etf',
          fund: 'mutual_fund',
        };

        const payload = {
          benchmark: {
            target_asset: benchmark,
          },
          holdings: filteredPositions,
        };

        const analysisData = await getAnalysis(
          {
            payload,
            params: { hist_w: hist },
            assetType: mapAssetType[assetType],
          },
          true,
        ).unwrap();
        /* istanbul ignore next */
        runSafeUpdate(() => {
          setAssetAnalysis(prevState => ({
            ...prevState,
            [assetType]: analysisData ?? null,
          }));
        });
      }
    } catch (error) {
      /* istanbul ignore next */
      console.error('[useAggregatorAllocationAssetAnalysis]:', error);
    }
  }, [hist, benchmark, getAnalysis, groupedPositions, runSafeUpdate]);

  const isLoading = useMemo(() => {
    return analysisQuery.isLoading || analysisQuery.isFetching;
  }, [analysisQuery.isLoading, analysisQuery.isFetching]);

  useEffect(() => {
    if (holdings.length > 0 && benchmark.length > 0) {
      getAnalysisData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hist, holdings.length, benchmark.length]);

  return {
    assetAnalysis,
    isLoading,
    getAnalysisData,
  };
};
